// src/modules/stock/RawMaterialReportPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../../firebaseConfig';
import {
    collection, getDocs, addDoc, deleteDoc, doc,
    serverTimestamp, query, orderBy
} from 'firebase/firestore';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal, ConfirmModal, PageHeader, Toast
} from '../../components/common';

// ✅ Stylesheet padrão para páginas de cadastro
import styles from '../Registers/Registers.module.css';

// React Icons — mesmo estilo do RawMaterialsPage
import {
    FiSearch, FiPackage, FiTag, FiTrash2
} from 'react-icons/fi';
import { FaTags, FaBuilding } from 'react-icons/fa';

// ============ Constants ============
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const REPORT_HISTORY_COLLECTION = 'raw-material-report-history';
const NCM_COLLECTION = 'parameters/data/ncm';
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const PRODUCT_TYPE_PATH = { parent: 'parameters', parentId: 'types', sub: 'tipo-produto' };

const REPORT_TYPES = { ALL: 'all', SINGLE: 'single' };

const INITIAL_CONFIG = {
    reportType: REPORT_TYPES.ALL,
    selectedMaterialId: '',
    // Seções a incluir
    includeSuppliers: true,
    includeFiscalInfo: true,
    includePricing: true,
    includeStockInfo: true,
    // ✅ ÚNICOS filtros necessários
    selectedProductTypeIds: [],
    selectedCategoryIds: []
};

// ============ Component ============
const RawMaterialReportPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [config, setConfig] = useState(INITIAL_CONFIG);

    const { data: rawMaterials, loading: loadingMaterials } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');
    const { data: ncmList } = useFirestore(NCM_COLLECTION, 'ncmCode');
    const { data: categories } = useFirestore(CATEGORY_COLLECTION, 'order');
    const { data: productTypes } = useSubCollection(
        PRODUCT_TYPE_PATH.parent,
        PRODUCT_TYPE_PATH.parentId,
        PRODUCT_TYPE_PATH.sub,
        'name'
    );

    useEffect(() => {
        const unsub = auth.onAuthStateChanged(u => setCurrentUser(u));
        return () => unsub();
    }, []);

    useEffect(() => { loadReports(); }, []);

    const addToast = (msg, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, msg, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, REPORT_HISTORY_COLLECTION), orderBy('generatedAt', 'desc'));
            const snap = await getDocs(q);
            setReports(snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                generatedAt: d.data().generatedAt?.toDate() || new Date()
            })));
        } catch (e) {
            console.error(e);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => { setReportToDelete(id); setIsConfirmOpen(true); };
    const deleteReport = async () => {
        if (!reportToDelete) return;
        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(prev => prev.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (e) {
            console.error(e);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // ============ Helpers ============
    const getNcmInfo = useCallback((ncmId) => {
        if (!ncmId || !ncmList) return { code: 'N/A', description: '' };
        const item = ncmList.find(n => n.id === ncmId || n.ncmCode === ncmId);
        return { code: item?.ncmCode || ncmId || 'N/A', description: item?.ncmDescription || '' };
    }, [ncmList]);

    const getParamNames = useCallback((ids, list) => {
        if (!ids || !list) return 'N/A';
        return ids.map(id => list.find(x => x.id === id)?.name || '').filter(Boolean).join(', ') || 'N/A';
    }, []);

    // ============ Filtering — only Product Type & Category ============
    const filteredMaterials = useMemo(() => {
        if (!rawMaterials) return [];
        let list = [...rawMaterials];

        if (config.selectedProductTypeIds.length > 0) {
            list = list.filter(m =>
                m.productTypeIds?.some(id => config.selectedProductTypeIds.includes(id))
            );
        }
        if (config.selectedCategoryIds.length > 0) {
            list = list.filter(m =>
                m.categoryIds?.some(id => config.selectedCategoryIds.includes(id))
            );
        }

        // Ordenação padrão: código
        list.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
        return list;
    }, [rawMaterials, config.selectedProductTypeIds, config.selectedCategoryIds]);

    const activeFilterCount = config.selectedProductTypeIds.length + config.selectedCategoryIds.length;

    const selectedMaterial = useMemo(
        () => config.selectedMaterialId ? rawMaterials?.find(m => m.id === config.selectedMaterialId) : null,
        [rawMaterials, config.selectedMaterialId]
    );

    // ============ Data builders (Excel) ============
    const generateMaterialData = useCallback((material) => {
        const ncm = getNcmInfo(material.ncm);
        return {
            basicInfo: {
                code: material.code || 'N/A',
                commercialName: material.commercialName || 'N/A',
                inciName: material.inciName || 'N/A',
                inciNamePt: material.inciNamePt || 'N/A',
                casNumber: material.casNumber || 'N/A',
                situation: material.situation || 'N/A',
                categories: getParamNames(material.categoryIds, categories),
                productTypes: getParamNames(material.productTypeIds, productTypes)
            },
            fiscalInfo: config.includeFiscalInfo ? {
                ncm: ncm.code,
                ncmDescription: ncm.description,
                origem: material.origemMercadoriaPadrao ?? 0,
                unidadeInterna: material.unidadeMedidaInterna || 'N/A',
                codigoBarras: material.codigoBarras || 'N/A'
            } : null,
            pricing: config.includePricing ? {
                custoCompra: material.custoCompra || 0,
                precoFormulacao: material.precoFormulacao || 0,
                moeda: material.moeda || 'BRL'
            } : null,
            stockInfo: config.includeStockInfo ? {
                minimo: material.qtdeMinimaEstoque || 0,
                maximo: material.qtdeMaximaEstoque || 0,
                passaQualidade: material.passaQualidade || false
            } : null,
            suppliers: config.includeSuppliers ? (material.suppliers || []) : []
        };
    }, [config, getNcmInfo, getParamNames, categories, productTypes]);

    const createSummarySheet = (sheet, list, ts) => {
        const cols = [
            { header: 'Código', key: 'code', width: 15 },
            { header: 'Nome Comercial', key: 'commercialName', width: 40 },
            { header: 'INCI Name', key: 'inciName', width: 40 },
            { header: 'INCI (PT)', key: 'inciNamePt', width: 35 },
            { header: 'CAS', key: 'casNumber', width: 18 },
            { header: 'NCM', key: 'ncm', width: 12 },
            { header: 'Descrição NCM', key: 'ncmDescription', width: 40 },
            { header: 'Situação', key: 'situation', width: 12 },
            { header: 'Tipo(s)', key: 'types', width: 30 },
            { header: 'Categoria(s)', key: 'categories', width: 30 },
            { header: 'Custo Compra (R$)', key: 'custoCompra', width: 18 }
        ];
        sheet.columns = cols;
        sheet.getCell('A1').value = `RELATÓRIO DE MATÉRIAS-PRIMAS - ${ts.toLocaleString('pt-BR')}`;
        sheet.getCell('A1').font = { size: 14, bold: true };
        sheet.mergeCells(`A1:${String.fromCharCode(64 + cols.length)}1`);

        list.forEach(m => {
            const ncm = getNcmInfo(m.ncm);
            sheet.addRow({
                code: m.code || 'N/A',
                commercialName: m.commercialName || 'N/A',
                inciName: m.inciName || 'N/A',
                inciNamePt: m.inciNamePt || 'N/A',
                casNumber: m.casNumber || 'N/A',
                ncm: ncm.code,
                ncmDescription: ncm.description,
                situation: m.situation || 'N/A',
                types: getParamNames(m.productTypeIds, productTypes),
                categories: getParamNames(m.categoryIds, categories),
                custoCompra: m.custoCompra?.toFixed(2) || '0.00'
            });
        });

        const headerRow = sheet.getRow(cols.length + 3);
        headerRow.eachCell(c => {
            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0B1F38' } };
            c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            c.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        sheet.eachRow(row => row.eachCell(c => {
            c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }));
    };

    const createSingleSheet = (sheet, material, data, ts) => {
        sheet.columns = [{ key: 'f', width: 35 }, { key: 'v', width: 55 }];
        let r = 1;
        sheet.getCell(`A${r}`).value = 'RELATÓRIO DE MATÉRIA-PRIMA';
        sheet.getCell(`A${r}`).font = { size: 18, bold: true };
        sheet.mergeCells(`A${r}:B${r}`); r += 2;
        sheet.getCell(`A${r}`).value = 'Data:';
        sheet.getCell(`B${r}`).value = ts.toLocaleString('pt-BR'); r += 2;

        const section = (title) => {
            sheet.getCell(`A${r}`).value = title;
            sheet.getCell(`A${r}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
            sheet.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0B1F38' } };
            sheet.mergeCells(`A${r}:B${r}`); r++;
        };
        const rows = (arr) => arr.forEach(([k, v]) => {
            sheet.getCell(`A${r}`).value = k;
            sheet.getCell(`B${r}`).value = v;
            r++;
        });

        section('INFORMAÇÕES BÁSICAS');
        rows([
            ['Código:', data.basicInfo.code],
            ['Nome Comercial:', data.basicInfo.commercialName],
            ['INCI Name:', data.basicInfo.inciName],
            ['INCI (PT):', data.basicInfo.inciNamePt],
            ['CAS:', data.basicInfo.casNumber],
            ['Situação:', data.basicInfo.situation],
            ['Tipo(s):', data.basicInfo.productTypes],
            ['Categoria(s):', data.basicInfo.categories]
        ]);
        r++;

        if (data.fiscalInfo) {
            section('INFORMAÇÕES FISCAIS');
            rows([
                ['NCM:', data.fiscalInfo.ncm],
                ['Descrição NCM:', data.fiscalInfo.ncmDescription],
                ['Origem:', data.fiscalInfo.origem],
                ['Unidade Interna:', data.fiscalInfo.unidadeInterna],
                ['Código de Barras:', data.fiscalInfo.codigoBarras]
            ]);
            r++;
        }
        if (data.pricing) {
            section('PREÇOS');
            rows([
                ['Custo Compra:', `R$ ${data.pricing.custoCompra.toFixed(2)}`],
                ['Preço Formulação:', `R$ ${data.pricing.precoFormulacao.toFixed(2)}`],
                ['Moeda:', data.pricing.moeda]
            ]);
            r++;
        }
        if (data.stockInfo) {
            section('ESTOQUE');
            rows([
                ['Qtde Mínima:', data.stockInfo.minimo],
                ['Qtde Máxima:', data.stockInfo.maximo],
                ['Passa Qualidade:', data.stockInfo.passaQualidade ? 'Sim' : 'Não']
            ]);
        }
        sheet.eachRow(row => row.eachCell(c => {
            c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }));
    };

    const createSuppliersSheet = (sheet, list, name = '') => {
        sheet.columns = [
            { header: 'Código', key: 'codigo', width: 15 },
            { header: 'Razão Social', key: 'razaoSocial', width: 45 },
            { header: 'Valor (R$)', key: 'valor', width: 18 }
        ];
        if (name) {
            sheet.getCell('A1').value = `Fornecedores - ${name}`;
            sheet.getCell('A1').font = { bold: true, size: 12 };
            sheet.mergeCells('A1:C1');
        }
        list.forEach(s => sheet.addRow({
            codigo: s.codigo || 'N/A',
            razaoSocial: s.razaoSocial || 'N/A',
            valor: s.valor?.toFixed(2) || '0.00'
        }));
        const headerRow = sheet.getRow(name ? 3 : 1);
        headerRow.eachCell(c => {
            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF27AE60' } };
            c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });
    };

    // ============ Generate ============
    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado', 'error');
            return;
        }
        if (config.reportType === REPORT_TYPES.SINGLE && !config.selectedMaterialId) {
            addToast('Selecione uma matéria-prima', 'warning'); return;
        }
        if (config.reportType === REPORT_TYPES.ALL && filteredMaterials.length === 0) {
            addToast('Nenhuma matéria-prima encontrada com os filtros selecionados', 'warning'); return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const ts = new Date();
            const tsStr = ts.toISOString().slice(0, 19).replace(/:/g, '-');
            const wb = new ExcelJS.Workbook();
            let fileName, itemCount;

            if (config.reportType === REPORT_TYPES.SINGLE && selectedMaterial) {
                const data = generateMaterialData(selectedMaterial);
                fileName = `Relatorio_MP_${selectedMaterial.code}_${tsStr}.xlsx`;
                itemCount = 1;
                createSingleSheet(wb.addWorksheet('Resumo'), selectedMaterial, data, ts);
                if (config.includeSuppliers && data.suppliers.length)
                    createSuppliersSheet(wb.addWorksheet('Fornecedores'), data.suppliers);
            } else {
                fileName = `Relatorio_MPs_${tsStr}.xlsx`;
                itemCount = filteredMaterials.length;
                createSummarySheet(wb.addWorksheet('Matérias-Primas'), filteredMaterials, ts);
                if (config.includeSuppliers) {
                    filteredMaterials.slice(0, 20).forEach(m => {
                        const d = generateMaterialData(m);
                        if (d.suppliers.length) {
                            const safe = (m.code || 'mp').replace(/[\\/*?:"<>|]/g, '_').slice(0, 31);
                            createSuppliersSheet(wb.addWorksheet(`${safe}_Forn`), d.suppliers, m.commercialName);
                        }
                    });
                }
            }

            const buffer = await wb.xlsx.writeBuffer();
            saveAs(new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }), fileName);

            const history = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                itemCount,
                reportType: config.reportType,
                config: {
                    reportType: config.reportType,
                    selectedMaterialId: config.selectedMaterialId,
                    selectedMaterialName: selectedMaterial?.commercialName || null,
                    selectedMaterialCode: selectedMaterial?.code || null,
                    includeSuppliers: config.includeSuppliers,
                    includeFiscalInfo: config.includeFiscalInfo,
                    includePricing: config.includePricing,
                    includeStockInfo: config.includeStockInfo,
                    selectedProductTypeIds: config.selectedProductTypeIds,
                    selectedCategoryIds: config.selectedCategoryIds,
                    selectedProductTypeNames: config.selectedProductTypeIds
                        .map(id => productTypes?.find(t => t.id === id)?.name)
                        .filter(Boolean),
                    selectedCategoryNames: config.selectedCategoryIds
                        .map(id => categories?.find(c => c.id === id)?.name)
                        .filter(Boolean),
                    activeFilterCount
                }
            };
            const ref = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), history);
            setReports(prev => [{ id: ref.id, ...history, generatedAt: new Date() }, ...prev]);
            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);
        } catch (err) {
            console.error(err);
            addToast(`Erro ao gerar relatório: ${err.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    // ============ Modal helpers ============
    const resetConfig = () => setConfig(INITIAL_CONFIG);
    const openModal = () => { resetConfig(); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); resetConfig(); };
    const change = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));

    const toggleProductType = (id) => setConfig(prev => ({
        ...prev,
        selectedProductTypeIds: prev.selectedProductTypeIds.includes(id)
            ? prev.selectedProductTypeIds.filter(x => x !== id)
            : [...prev.selectedProductTypeIds, id]
    }));

    const toggleCategory = (id) => setConfig(prev => ({
        ...prev,
        selectedCategoryIds: prev.selectedCategoryIds.includes(id)
            ? prev.selectedCategoryIds.filter(x => x !== id)
            : [...prev.selectedCategoryIds, id]
    }));

    // ============ Format helpers ============
    const formatDate = (d) => d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    const getReportTypeLabel = (t) => t === REPORT_TYPES.SINGLE ? 'Matéria-Prima' : 'Todas';

    const getConfigDescription = (c = {}) => {
        const parts = [];
        if (c.reportType === REPORT_TYPES.SINGLE && c.selectedMaterialName) {
            parts.push(`${c.selectedMaterialCode} - ${c.selectedMaterialName}`);
        }
        if (c.selectedProductTypeNames?.length) {
            parts.push(`Tipos: ${c.selectedProductTypeNames.join(', ')}`);
        }
        if (c.selectedCategoryNames?.length) {
            parts.push(`Categorias: ${c.selectedCategoryNames.join(', ')}`);
        }
        return parts.length ? parts.join(' | ') : 'Sem filtros';
    };

    // ============ Modal content (formulário seguindo o padrão do ERP) ============
    const renderModalContent = () => (
        <>
            {/* Seção: Tipo de Relatório */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                    <h3 className={styles.sectionTitle}>Configuração do Relatório</h3>
                </div>
                <div className={styles.formGrid} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                    <div className={styles.inputGroup}>
                        <label>Tipo de Relatório</label>
                        <select
                            className={styles.select}
                            value={config.reportType}
                            onChange={(e) => change('reportType', e.target.value)}
                        >
                            <option value={REPORT_TYPES.ALL}>Todos os Produtos</option>
                            <option value={REPORT_TYPES.SINGLE}>Produto Específico</option>
                        </select>
                    </div>
                    {config.reportType === REPORT_TYPES.SINGLE && (
                        <div className={styles.inputGroup}>
                            <label>Selecione a Matéria-Prima</label>
                            <select
                                className={styles.select}
                                value={config.selectedMaterialId}
                                onChange={(e) => change('selectedMaterialId', e.target.value)}
                                disabled={loadingMaterials}
                            >
                                <option value="">-- Selecione --</option>
                                {rawMaterials?.map(m => (
                                    <option key={m.id} value={m.id}>
                                        {m.code} - {m.commercialName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Seção: Filtros — só Tipo e Categoria, como checkboxes do ERP */}
            {config.reportType === REPORT_TYPES.ALL && (
                <>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FaTags size={18} /></div>
                            <h3 className={styles.sectionTitle}>Filtrar por Tipo de Produto</h3>
                            <span className={styles.sectionBadge}>
                                {config.selectedProductTypeIds.length} selecionado(s)
                            </span>
                        </div>
                        <div className={styles.checkboxGrid}>
                            {productTypes?.length ? productTypes.map(pt => (
                                <label key={pt.id} className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={config.selectedProductTypeIds.includes(pt.id)}
                                        onChange={() => toggleProductType(pt.id)}
                                    />
                                    <span className={styles.checkboxText}>{pt.name}</span>
                                </label>
                            )) : (
                                <p className={styles.textMuted}>Nenhum tipo cadastrado.</p>
                            )}
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTag size={18} /></div>
                            <h3 className={styles.sectionTitle}>Filtrar por Categoria</h3>
                            <span className={styles.sectionBadge}>
                                {config.selectedCategoryIds.length} selecionado(s)
                            </span>
                        </div>
                        <div className={styles.checkboxGrid}>
                            {categories?.length ? categories.map(c => (
                                <label key={c.id} className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={config.selectedCategoryIds.includes(c.id)}
                                        onChange={() => toggleCategory(c.id)}
                                    />
                                    <span className={styles.checkboxText}>{c.name}</span>
                                </label>
                            )) : (
                                <p className={styles.textMuted}>Nenhuma categoria cadastrada.</p>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Seção: Seções a incluir no arquivo */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                    <h3 className={styles.sectionTitle}>Seções a Incluir no Relatório</h3>
                </div>
                <div className={styles.checkboxGrid}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.includeSuppliers}
                            onChange={(e) => change('includeSuppliers', e.target.checked)}
                        />
                        <span className={styles.checkboxText}>Fornecedores</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.includeFiscalInfo}
                            onChange={(e) => change('includeFiscalInfo', e.target.checked)}
                        />
                        <span className={styles.checkboxText}>Informações Fiscais</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.includePricing}
                            onChange={(e) => change('includePricing', e.target.checked)}
                        />
                        <span className={styles.checkboxText}>Informações de Preço</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={config.includeStockInfo}
                            onChange={(e) => change('includeStockInfo', e.target.checked)}
                        />
                        <span className={styles.checkboxText}>Informações de Estoque</span>
                    </label>
                </div>
            </div>

            {/* Preview */}
            {config.reportType === REPORT_TYPES.ALL && (
                <div className={styles.boxTypeInfo}>
                    <strong>
                        📊 {filteredMaterials.length} matéria(s)-prima(s) serão incluídas
                        {activeFilterCount > 0 && ` (${activeFilterCount} filtro(s) ativo(s))`}
                    </strong>
                </div>
            )}
            {config.reportType === REPORT_TYPES.SINGLE && selectedMaterial && (
                <div className={styles.boxTypeInfo}>
                    <strong>
                        📄 {selectedMaterial.code} - {selectedMaterial.commercialName}
                    </strong>
                </div>
            )}
        </>
    );

    // ============ Render ============
    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(t => (
                        <Toast
                            key={t.id}
                            message={t.msg}
                            type={t.type}
                            onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                        />
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Configurar Relatório de Matérias-Primas"
                subtitle="Selecione os filtros e as seções desejadas"
                isLoading={generating}
                submitText="Gerar Relatório"
                cancelText="Cancelar"
                maxWidth="900px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Page Header */}
            <PageHeader
                title="Relatório de Matérias-Primas"
                subtitle="Relatórios com código, nome comercial, INCI name e NCM"
                actionText="Novo Relatório"
                onAction={openModal}
                disabled={!currentUser || loadingMaterials}
            />

            {/* Tabela de histórico — mesmo padrão do RawMaterialsPage */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '160px' }}>Data/Hora</th>
                            <th>Arquivo</th>
                            <th style={{ width: '200px' }}>Gerado por</th>
                            <th style={{ width: '80px' }}>Itens</th>
                            <th style={{ width: '120px' }}>Tipo</th>
                            <th style={{ width: '280px' }}>Configuração</th>
                            <th style={{ width: '90px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className={styles.emptyTable}>
                                    Carregando histórico...
                                </td>
                            </tr>
                        ) : reports.length === 0 ? (
                            <tr>
                                <td colSpan={7} className={styles.emptyTable}>
                                    Nenhum relatório gerado ainda. Clique em "Novo Relatório" para começar.
                                </td>
                            </tr>
                        ) : (
                            reports.map(r => (
                                <tr key={r.id} className={styles.tableRow}>
                                    <td>{formatDate(r.generatedAt)}</td>
                                    <td>
                                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                            {r.fileName}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontWeight: 500 }}>
                                                {r.generatedByName || r.generatedBy?.split('@')[0] || 'Usuário'}
                                            </span>
                                            {r.generatedByEmail && (
                                                <span style={{ fontSize: '11px', color: 'var(--reg-text-muted)' }}>
                                                    {r.generatedByEmail}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{r.itemCount}</td>
                                    <td style={{ fontSize: '12px' }}>{getReportTypeLabel(r.reportType)}</td>
                                    <td style={{ fontSize: '12px' }}>{getConfigDescription(r.config)}</td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            <button
                                                onClick={() => handleDeleteClick(r.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                title="Excluir"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RawMaterialReportPage;